/*
 * Host-page half of the widget. Runs in the customer's page and defines the
 * <constell-widget> custom element the customer already embeds. Instead of mounting
 * the Vue app directly (Shadow DOM only), it renders the app inside a cross-origin,
 * fully transparent <iframe> pointing at our own `frame.html`, giving the widget a
 * completely separate document + JS context. Host-page scripts therefore cannot reach
 * the widget's DOM, CSS or events - the root cause of the focus-trap / analytics-
 * interference bugs on customer sites.
 *
 * To keep the public contract identical, this shell:
 *   - forwards its attributes into the iframe (initial load + reactive changes),
 *   - re-exposes `window.CONSTELL` on the host window and forwards calls to the frame,
 *   - re-emits analytics events on the host `window` + `dataLayer`,
 *   - keeps the iframe at full viewport size and clips it to the launcher while closed.
 *
 * The iframe is transparent (no background on the element or the framed document, plus
 * `color-scheme: normal` so the browser paints no default canvas), so the page shows
 * through wherever the widget itself does not draw.
 */
import {
    type BridgeCommandTyping,
    type BridgeReservationTyping,
    type BridgeResizeTyping,
    type BridgeTrackTyping,
    buildMessage,
    FRAME_TO_HOST,
    HOST_TO_FRAME,
    readBridgeMessage,
} from './bridge';
import { openReservationProvider } from '@/helpers/reservation-openers';
import { constellTrack } from '@/helpers/tracking';

// Attributes mirrored into the iframe. Kept in sync with WidgetProps / AppLoader.
const FORWARDED_ATTRIBUTES = [
    'venue',
    'button-label',
    'button-label-close',
    'locale',
    'position',
    'offset-x',
    'offset-y',
    'brand-color',
    'min-guest-amount',
    'max-guest-amount',
    'guest-amount-step',
    'max-budget-amount',
    'min-budget-amount',
    'budget-step',
    'show-table-button',
    'show-hotel-button',
    'show-gift-button',
] as const;

// Highest usable z-index so the widget always sits above host-page content.
const TOP_LAYER_Z_INDEX = '2147483647';

const buildFrameSrc = (bundleUrl: string, attributes: Record<string, string>): string => {
    const frameUrl = new URL('../frame.html', bundleUrl);
    frameUrl.searchParams.set('b', bundleUrl);

    Object.entries(attributes)
        .forEach(([key, value]) => { frameUrl.searchParams.set(key, value) });

    return frameUrl.href;
};

class ConstellWidgetHostElement extends HTMLElement {
    static get observedAttributes(): readonly string[] {
        return FORWARDED_ATTRIBUTES;
    }

    // Bundle URL is injected once by main.ts so the shell can resolve `frame.html`
    // (and pass its own bundle to the frame) relative to the deployed asset URL.
    static bundleUrl = '';

    private iframe: HTMLIFrameElement | null = null;
    private bridgeOrigin = '';
    private readonly onMessage = (event: MessageEvent): void => { this.handleMessage(event) };

    connectedCallback(): void {
        if (this.iframe) return;

        const { bundleUrl } = ConstellWidgetHostElement;
        const src = buildFrameSrc(bundleUrl, this.collectAttributes());
        this.bridgeOrigin = new URL(bundleUrl).origin;

        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.title = 'Constell';
        iframe.setAttribute('allow', 'clipboard-write');
        iframe.setAttribute('allowtransparency', 'true');
        iframe.style.cssText = [
            'position: fixed',
            'border: 0',
            'margin: 0',
            'padding: 0',
            'background: transparent',
            'color-scheme: normal',
            `z-index: ${TOP_LAYER_Z_INDEX}`,

            // Always the full viewport (see applyGeometry); the clip decides what of it
            // actually paints and receives pointer events.
            'inset: 0',
            'width: 100vw',
            'height: 100dvh',

            // Start fully clipped until the frame reports its launcher box, so we never
            // flash a wrongly-sized box over the page or swallow clicks.
            'clip-path: inset(100%)',
        ].join(';');

        this.iframe = iframe;
        this.appendChild(iframe);

        window.addEventListener('message', this.onMessage);
        this.registerGlobalApi();
    }

    disconnectedCallback(): void {
        window.removeEventListener('message', this.onMessage);
        this.iframe?.remove();
        this.iframe = null;
    }

    attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
        // Forward reactive attribute changes (e.g. locale) into the running frame.
        this.postToFrame(HOST_TO_FRAME.CONFIG, {
            name,
            value: value ?? '',
        });
    }

    private collectAttributes(): Record<string, string> {
        const attributes: Record<string, string> = {};

        FORWARDED_ATTRIBUTES.forEach(name => {
            const value = this.getAttribute(name);
            if (value !== null) attributes[name] = value;
        });

        return attributes;
    }

    private postToFrame(type: string, payload?: unknown): void {
        const target = this.iframe?.contentWindow;
        if (!target || !this.bridgeOrigin) return;

        target.postMessage(buildMessage(type, payload), this.bridgeOrigin);
    }

    private handleMessage(event: MessageEvent): void {
        if (event.source !== this.iframe?.contentWindow) return;

        const message = readBridgeMessage(event, this.bridgeOrigin);
        if (!message) return;

        switch (message.type) {
            case FRAME_TO_HOST.RESIZE:
                this.applyGeometry(message.payload as BridgeResizeTyping);
                break;
            case FRAME_TO_HOST.TRACK: {
                const { name, detail } = message.payload as BridgeTrackTyping;

                // Re-emit on the HOST window + dataLayer so customer listeners / GTM
                // still receive events exactly as before the iframe move.
                constellTrack(name, detail);
                break;
            }
            case FRAME_TO_HOST.OPEN_RESERVATION: {
                // Run the 3rd-party opener here, where the provider SDKs (FT/Gifty/_gstpln,
                // Zenchef/Mews DOM triggers) actually live - the frame can't reach them.
                const { partnerId } = message.payload as BridgeReservationTyping;
                openReservationProvider(partnerId);
                break;
            }
            default:
                break;
        }
    }

    /*
     * The iframe always spans the full viewport, so CSS inside the frame resolves against
     * the SAME viewport as the host page - a launcher-sized iframe would drop every
     * width-based media query (and the responsive font/spacing tokens built on them) to
     * the mobile tier, which is why the framed widget rendered smaller text than the
     * in-page webcomponent. While closed we clip the iframe to the launcher box instead of
     * resizing it; `clip-path` clips hit testing too, so the host page stays clickable
     * everywhere outside that box.
     */
    private applyGeometry(geometry: BridgeResizeTyping): void {
        if (!this.iframe) return;

        const { open, top, left, width, height } = geometry;

        if (open) {
            this.iframe.style.clipPath = 'none';
            return;
        }

        if (width <= 0 || height <= 0) {
            this.iframe.style.clipPath = 'inset(100%)';
            return;
        }

        // inset() takes distances from each edge, so convert the box and clamp: negative
        // insets are invalid and would drop the clip (making the whole page unclickable).
        const insets = [
            Math.max(0, top),
            Math.max(0, window.innerWidth - (left + width)),
            Math.max(0, window.innerHeight - (top + height)),
            Math.max(0, left),
        ];

        this.iframe.style.clipPath = `inset(${insets.map(value => `${Math.floor(value)}px`)
            .join(' ')})`;
    }

    private registerGlobalApi(): void {
        const post = (payload: BridgeCommandTyping): void => { this.postToFrame(HOST_TO_FRAME.COMMAND, payload) };

        (window as unknown as Record<string, unknown>).CONSTELL = {
            open: () => { post({ command: 'open' }) },
            close: () => { post({ command: 'close' }) },
            toggle: () => { post({ command: 'toggle' }) },
            navigate: (target: string, payload?: Record<string, unknown>) => {
                post({
                    command: 'navigate',
                    target,
                    payload,
                });
            },
        };
    }
}

export const defineHostShell = (bundleUrl: string): void => {
    ConstellWidgetHostElement.bundleUrl = bundleUrl;

    if (!customElements.get('constell-widget')) {
        customElements.define('constell-widget', ConstellWidgetHostElement);
    }
};
